import clsx from 'clsx'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Temporal } from 'temporal-polyfill'

export interface AppointmentSlot {
  timestamp: string
  recurrenceCount: number
}

interface AppointmentSelectorProps {
  slots: AppointmentSlot[]
  selectedTimestamp?: string
  description?: React.ReactNode
  locale?: string
  onSelect: (slot: AppointmentSlot) => void
}

export function AppointmentSelector({
  slots,
  selectedTimestamp,
  description,
  locale = 'en',
  onSelect,
}: AppointmentSelectorProps) {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [startingDateOffset, setStartingDateOffset] = useState<number>(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleDays, setVisibleDays] = useState(1) // Start with 1 visible day
  const maxVisibleSlotsPerDay = 3

  useEffect(() => {
    const updateVisibleDays = () => {
      if (!containerRef.current) return
      const width = containerRef.current.offsetWidth

      if (width >= 920) setVisibleDays(7)
      else if (width >= 800) setVisibleDays(7)
      else if (width >= 680) setVisibleDays(6)
      else if (width >= 560) setVisibleDays(5)
      else if (width >= 440) setVisibleDays(4)
      else if (width >= 320) setVisibleDays(3)
      else setVisibleDays(2)
    }

    const observer = new ResizeObserver(updateVisibleDays)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const localizedStrings = {
    en: {
      showLess: '- Show less',
      showMore: '+ Show more',
    },
    es: {
      showLess: '- Mostrar menos',
      showMore: '+ Mostrar más',
    },
    pt: {
      showLess: '- Mostrar menos',
      showMore: '+ Mostrar mais',
    },
  }

  const currentLocale = (locale as 'en' | 'es' | 'pt') || 'en'
  const stringsForLocale = localizedStrings[currentLocale] || localizedStrings.en

  const slotsByDate = useMemo(() => {
    const slotsMap = new Map<string, AppointmentSlot[]>()

    if (slots.length === 0) {
      return slotsMap
    }

    // Sort slots by date
    const sortedSlots = [...slots].sort((a, b) =>
      Temporal.ZonedDateTime.compare(
        Temporal.ZonedDateTime.from(a.timestamp),
        Temporal.ZonedDateTime.from(b.timestamp)
      )
    )

    const firstSlot = sortedSlots[0]
    const lastSlot = sortedSlots[sortedSlots.length - 1]

    if (!firstSlot || !lastSlot) {
      return slotsMap
    }

    const startDate = Temporal.ZonedDateTime.from(firstSlot.timestamp).toPlainDate()
    let endDate = Temporal.ZonedDateTime.from(lastSlot.timestamp).toPlainDate()
    const dateRangeLength = endDate.since(startDate, { largestUnit: 'days' }).days + 1

    if (dateRangeLength % visibleDays !== 0) {
      const daysToPad = visibleDays - (dateRangeLength % visibleDays)
      endDate = endDate.add({ days: daysToPad })
    }

    // Initialize map with all dates in range
    let currentDate = startDate
    while (Temporal.PlainDate.compare(currentDate, endDate) <= 0) {
      slotsMap.set(currentDate.toString(), [])
      currentDate = currentDate.add({ days: 1 })
    }

    // Add slots to their respective dates
    sortedSlots.forEach((slot) => {
      const date = Temporal.ZonedDateTime.from(slot.timestamp).toPlainDate()
      const key = date.toString()
      slotsMap.get(key)?.push(slot)
    })

    return slotsMap
  }, [slots, visibleDays])

  const maxStartingDateOffset = Math.max(0, slotsByDate.size - visibleDays)
  const previousButtonDisabled = startingDateOffset === 0
  const nextButtonDisabled = startingDateOffset === maxStartingDateOffset

  const allowExpansion = useMemo(() => {
    return (
      Math.max(...Array.from(slotsByDate.values()).map((events) => events.length)) >
      maxVisibleSlotsPerDay
    )
  }, [slotsByDate])

  const onPreviousButtonClicked = () => {
    setStartingDateOffset(Math.max(0, startingDateOffset - visibleDays))
  }

  const onNextButtonClicked = () => {
    setStartingDateOffset(Math.min(maxStartingDateOffset, startingDateOffset + visibleDays))
  }

  const onSlotClicked = (slot: AppointmentSlot) => {
    onSelect(slot)
  }

  const labelForStartingDate = () => {
    const timestamp = Array.from(slotsByDate.keys())[startingDateOffset]
    if (!timestamp) {
      return ''
    }

    const date = Temporal.PlainDate.from(timestamp)
    const formatted_date = date.toLocaleString(currentLocale, { month: 'long', year: 'numeric' })

    return formatted_date.charAt(0).toUpperCase() + formatted_date.slice(1)
  }

  const labelForDateColumn = (dateString: string, isDisabled: boolean) => {
    var date = Temporal.PlainDate.from(dateString)

    return (
      <div
        className={
          'flex flex-col items-center justify-center relative' +
          (isDisabled ? ' text-charcoal-25 line-through' : ' text-charcoal')
        }
      >
        <p className="text-xs font-semibold uppercase leading-none relative z-1">
          {date.toLocaleString(currentLocale, { weekday: 'long' }).slice(0, 3)}
        </p>
        <h3 className="text-[20px] font-medium font-ginto-nord-md relative z-1">{date.day}</h3>
      </div>
    )
  }

  const buttonForSlot = (slot: AppointmentSlot) => {
    const isSelected = slot.timestamp === selectedTimestamp
    const plainTime = Temporal.ZonedDateTime.from(slot.timestamp).toPlainTime()

    let className =
      'h-10 border-[3px] font-sans font-medium px-2.5 sm:px-4 py-2 rounded-lg whitespace-nowrap no-underline flex items-center justify-center leading-none text-xs text-charcoal hover:cursor-pointer'
    if (isSelected) {
      className += ' bg-mint-50 border-forest'
    } else {
      className += ' bg-sand border-sand hover:bg-sand-dark hover:border-sand-dark'
    }

    return (
      <button
        key={slot.timestamp}
        className={className}
        disabled={isSelected}
        onClick={() => {
          onSlotClicked(slot)
        }}
        type="button"
      >
        {plainTime.toLocaleString(currentLocale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        })}
      </button>
    )
  }

  const navigationButtonClass = (isDisabled: boolean) =>
    clsx(
      'h-10 border-[3px] font-sans font-medium text-base p-2 rounded-lg whitespace-nowrap no-underline flex items-center justify-center leading-none bg-transparent border-transparent',
      {
        'text-charcoal-25': isDisabled,
        'text-forest hover:bg-sand hover:border-sand': !isDisabled,
      }
    )

  return (
    <div className="w-full" ref={containerRef}>
      <div className="flex items-center justify-center self-stretch relative mt-4">
        <h3 className="text-[20px] text-charcoal font-medium font-ginto-nord-md">
          {labelForStartingDate()}
        </h3>
      </div>

      <div className="text-center text-charcoal-50 whitespace-normal pb-4">{description}</div>

      <div className="flex flex-row gap-2 justify-between">
        <div className="flex flex-col">
          <button
            onClick={onPreviousButtonClicked}
            disabled={previousButtonDisabled}
            className={navigationButtonClass(previousButtonDisabled)}
            type="button"
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>
        </div>
        {Array.from(slotsByDate.entries())
          .slice(startingDateOffset, startingDateOffset + visibleDays)
          .map(([dateString, slots]) => (
            <div className="flex flex-col gap-3 flex-1" key={dateString}>
              <div className="h-10 flex justify-center items-center">
                {labelForDateColumn(dateString, slots.length === 0)}
              </div>
              {slots
                .filter((_slot, index) => expanded || index < maxVisibleSlotsPerDay)
                .map((slot) => buttonForSlot(slot))}
            </div>
          ))}
        <div className="flex flex-col">
          <button
            onClick={onNextButtonClicked}
            disabled={nextButtonDisabled}
            className={navigationButtonClass(nextButtonDisabled)}
            type="button"
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
      {allowExpansion && (
        <div className="flex flex-row justify-center items-center p-4">
          <button
            className="text-indigo underline font-semibold"
            onClick={() => setExpanded(!expanded)}
            type="button"
          >
            {expanded ? stringsForLocale.showLess : stringsForLocale.showMore}
          </button>
        </div>
      )}
    </div>
  )
}
